import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Play, X, ChevronLeft, ChevronRight } from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import Breadcrumbs from '../../components/masterclass/Breadcrumbs'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'

const VIDEO_CATEGORIES = [
  'All', 'Masterclass Highlights', 'Full Sessions', 'Student Stories', 'Behind The Scenes', 'Tutorials', 'Speaker Interviews'
]

function getYouTubeEmbed(youtubeId) {
  return `https://www.youtube.com/embed/${youtubeId}`
}

function VideoModal({ video, onClose }) {
  useEffect(() => {
    const handleKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      <div className="w-full max-w-5xl aspect-video bg-black" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={getYouTubeEmbed(video.youtube_id)}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    </div>
  )
}

function VideoCard({ video, onClick, featured }) {
  const thumb = `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`
  return (
    <button onClick={onClick} className={`group block w-full text-left ${featured ? 'aspect-video' : ''}`}>
      <div className="relative overflow-hidden h-full flex flex-col group">
        <div className="relative aspect-video overflow-hidden">
          <img src={thumb} alt={video.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
            <div className="w-14 h-14 rounded-full bg-cinema-glow/90 flex items-center justify-center transition-transform group-hover:scale-110">
              <Play className="w-6 h-6 text-white fill-white ml-1" />
            </div>
          </div>
          {video.duration && (
            <div className="absolute bottom-3 right-3 bg-black/70 px-2 py-1 text-xs text-white font-sans">
              {video.duration}
            </div>
          )}
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <p className="text-white font-sans text-sm font-medium line-clamp-2 group-hover:text-cinema-gold transition-colors">
            {video.title}
          </p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-cinema-muted text-xs">{video.category || 'Highlight'}</span>
            {video.speaker_name && <span className="text-cinema-gold text-xs font-sans">{video.speaker_name}</span>}
          </div>
        </div>
      </div>
    </button>
  )
}

export default function VideosPage() {
  const [videos, setVideos] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('All')
  const [search, setSearch] = useState('')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    document.title = `Video Library | ${SCHEDULE_SERIES_NAME}`
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    masterclassService.getVideos({ limit: 200 })
      .then((res) => {
        setVideos(res.videos || [])
        setFiltered(res.videos || [])
      })
      .catch(() => {
        setVideos([])
        setFiltered([])
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    let result = videos
    if (activeCategory !== 'All') {
      result = result.filter(v => v.category === activeCategory)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(v =>
        v.title.toLowerCase().includes(q) ||
        (v.description || '').toLowerCase().includes(q) ||
        (v.speaker_name || '').toLowerCase().includes(q)
      )
    }
    setFiltered(result)
  }, [activeCategory, search, videos])

  const featured = filtered.find(v => v.is_featured) || filtered[0]
  const groups = filtered.reduce((acc, video) => {
    const cat = video.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(video)
    return acc
  }, {})

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="cinema-glow-orb w-[700px] h-[700px] -top-40 right-0 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs />
          </Reveal>
          <Reveal delay={100}>
            <span className="cinema-label mt-10 block">On Demand</span>
            <h1 className="cinema-headline-lg mt-6">
              Video <span className="text-cinema-gold">Library</span>
            </h1>
            <p className="cinema-body-lg mt-6 max-w-2xl text-cinema-muted">
              Replay the best moments, full sessions, tutorials, and speaker interviews from the {SCHEDULE_SERIES_NAME} series.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured */}
      {!loading && featured && (
        <section className="bg-cinema-charcoal py-12">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Featured</span>
              <div className="mt-6 grid lg:grid-cols-2 gap-8 items-center">
                <button
                  onClick={() => setSelectedVideo(featured)}
                  className="relative aspect-video overflow-hidden group"
                >
                  <img
                    src={`https://img.youtube.com/vi/${featured.youtube_id}/maxresdefault.jpg`}
                    alt={featured.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-20 h-20 rounded-full bg-cinema-glow/90 flex items-center justify-center transition-transform group-hover:scale-110">
                      <Play className="w-8 h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                </button>
                <div>
                  <h2 className="font-display text-3xl text-white font-medium leading-tight">{featured.title}</h2>
                  <p className="text-cinema-gold font-sans mt-3">{featured.category || 'Highlight'}</p>
                  {featured.description && <p className="text-cinema-muted font-sans mt-4 leading-relaxed">{featured.description}</p>}
                  <button
                    onClick={() => setSelectedVideo(featured)}
                    className="mt-8 cinema-btn-primary group"
                  >
                    <Play className="w-4 h-4 fill-white" /> Watch Now
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-cinema-black/90 backdrop-blur-md border-b border-white/5 py-4">
        <div className="cinema-container">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 no-scrollbar">
              {VIDEO_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-4 py-2 text-xs font-sans uppercase tracking-wider transition-all ${
                    activeCategory === cat
                      ? 'text-cinema-glow'
                      : 'text-cinema-muted hover:text-cinema-gold'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative w-full lg:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search videos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-cinema-graphite border-b border-white/10 text-white pl-10 pr-4 py-2.5 text-sm font-sans focus:outline-none focus:border-cinema-gold/40 transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Rows */}
      <section className="bg-cinema-black py-24 sm:py-32 space-y-20">
        <div className="cinema-container">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-cinema-graphite/50 aspect-video animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6">🎬</div>
              <h3 className="font-display text-2xl text-white">No videos found</h3>
              <p className="text-cinema-muted font-sans mt-3">Videos will be added after each masterclass.</p>
            </div>
          ) : (
            Object.entries(groups).map(([category, items]) => (
              <div key={category}>
                <Reveal>
                  <h2 className="font-display text-2xl text-white mb-6 flex items-center gap-3">
                    {category}
                    <span className="text-cinema-muted text-sm font-sans font-normal">{items.length}</span>
                  </h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {items.map((video, i) => (
                    <Reveal key={video.id} delay={i * 60}>
                      <VideoCard video={video} onClick={() => setSelectedVideo(video)} />
                    </Reveal>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />

      {selectedVideo && <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />}
    </div>
  )
}
